
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import {
  HiOutlineArrowUpRight,
  HiOutlineBookOpen,
  HiOutlineFolderOpen,
} from "react-icons/hi2";

import { media } from "../../server";

/* ============================================
   IMAGE URL CANDIDATES
============================================ */

function getCategoryImageSources(image) {
  if (!image || typeof image !== "string") {
    return [];
  }

  const original = image.trim().replace(/\\/g, "/");

  if (!original) return [];

  // Already a full URL
  if (/^(https?:\/\/|data:image\/|blob:)/i.test(original)) {
    return [original];
  }

  const path = original.replace(/^\/+/, "");

  // Prefer WebP when older records still contain PNG/JPG
  const webpPath = path.replace(
    /\.(png|jpe?g)$/i,
    ".webp"
  );

  const paths = [...new Set([webpPath, path])];

  const base = String(media || "").replace(/\/+$/, "");

  const sources = paths.flatMap((filePath) => {
    const filename = filePath.split("/").pop();

    return [
      // Backend media
      base
        ? `${base}/${filePath}`
        : `/${filePath}`,

      // Frontend public folder
      `/images/${filename}`,

      // Frontend public root
      `/${filePath}`,
    ];
  });

  return [...new Set(sources)];
}

/* ============================================
   TEXT CLEANUP
============================================ */

function getPlainText(value) {
  if (typeof value !== "string") return "";

  const normalized = value.replace(
    /<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi,
    " "
  );

  const safe = DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  const element = document.createElement("div");
  element.innerHTML = safe;

  return (element.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ============================================
   CATEGORY IMAGE
============================================ */

function CategoryImage({ image, alt }) {
  const sources = useMemo(
    () => getCategoryImageSources(image),
    [image]
  );

  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [image]);

  const currentSource = sources[sourceIndex];
  const hasImage = Boolean(currentSource);

  const handleImageError = () => {
    setSourceIndex((previous) => previous + 1);
  };

  return (
    <div
      className="
        relative h-full w-full
        overflow-hidden
        bg-[#19344D]
      "
    >
      {hasImage ? (
        <img
          src={currentSource}
          alt={alt || ""}
          loading="lazy"
          decoding="async"
          onError={handleImageError}
          className="
            absolute inset-0
            block h-full w-full

            object-cover object-center

            transition-transform
            duration-700 ease-out

            group-hover:scale-[1.055]
          "
        />
      ) : (
        <div
          className="
            absolute inset-0
            flex items-center justify-center

            bg-gradient-to-br
            from-[#294961]
            via-[#18354A]
            to-[#0B1726]
          "
        >
          {/* Decorative outlines */}
          <div
            aria-hidden="true"
            className="
              absolute -end-16 -top-16
              h-56 w-56 rounded-full
              border border-white/10
            "
          />

          <div
            aria-hidden="true"
            className="
              absolute -start-16 -bottom-20
              h-52 w-52 rounded-full
              border border-white/10
            "
          />

          <HiOutlineBookOpen
            size={60}
            aria-hidden="true"
            className="relative text-[#D7B77F]/65"
          />
        </div>
      )}

      {/* Gentle gradient to improve visual consistency */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0
          bg-gradient-to-t
          from-[#081827]/30
          via-transparent
          to-transparent
        "
      />
    </div>
  );
}

/* ============================================
   BLOG CATEGORY CARD
============================================ */

function BlogCategoryCard({
  category,
  index = 0,
}) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = isEnglish
    ? category?.titleEn || category?.titleAr
    : category?.titleAr || category?.titleEn;

  const rawDescription = isEnglish
    ? category?.textEn || category?.textAr
    : category?.textAr || category?.textEn;

  const description = useMemo(
    () => getPlainText(rawDescription),
    [rawDescription]
  );

  const labels = {
    category: isEnglish
      ? "LEGAL CATEGORY"
      : "تصنيف قانوني",

    explore: isEnglish
      ? "Explore Articles"
      : "استعرض المقالات",

    empty: isEnglish
      ? "Discover legal articles in this category."
      : "اكتشف المقالات المرتبطة بهذا التصنيف.",
  };

  const number = String(index + 1).padStart(2, "0");

  return (
    <Link
      to="/AllBlogs"
      state={{ category }}
      onClick={() => window.scrollTo(0, 0)}
      aria-label={`${labels.explore}: ${title}`}
      className="
        group flex h-full min-w-0
        flex-col overflow-hidden

        rounded-[22px]

        border border-[#DCE5EB]
        bg-white

        shadow-[0_10px_35px_rgba(15,39,55,0.05)]

        transition-all duration-300

        hover:-translate-y-1.5
        hover:border-[#C19B60]/45
        hover:shadow-[0_20px_50px_rgba(15,39,55,0.12)]

        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-skin-accent

        dark:border-white/10
        dark:bg-[#1B3549]
        dark:shadow-none

        dark:hover:border-[#D7B77F]/35
      "
    >
      {/* ======================================
          IMAGE
      ====================================== */}

      <div
        className="
          relative aspect-[16/10]
          w-full shrink-0
          overflow-hidden
        "
      >
        <CategoryImage
          image={category?.Image}
          alt={title}
        />

        {/* Number */}
        <span
          className="
            absolute end-4 top-4

            inline-flex h-9 min-w-9
            items-center justify-center

            rounded-lg
            border border-white/20
            bg-[#102031]/65

            px-3
            font-cairo text-xs font-bold
            tabular-nums text-white

            backdrop-blur-lg
          "
        >
          {number}
        </span>

        {/* Small folder emblem */}
        <span
          className="
            absolute bottom-4 start-5

            flex h-11 w-11
            items-center justify-center

            rounded-xl
            border border-white/20
            bg-[#102031]/75

            text-[#D7B77F]
            backdrop-blur-lg
          "
        >
          <HiOutlineFolderOpen
            size={23}
            aria-hidden="true"
          />
        </span>

      </div>

      {/* ======================================
          CARD CONTENT
      ====================================== */}

      <div
        className="
          flex flex-1 flex-col
          px-6 pb-6 pt-7
          sm:px-7 sm:pb-7
        "
      >
        {/* Eyebrow */}
        <div className="mb-4 flex items-center gap-3">

          <span
            aria-hidden="true"
            className="h-px w-7 bg-skin-accent"
          />

          <span className="eyebrow text-skin-accent">
            {labels.category}
          </span>
        </div>

        {/* Category title */}
        <h3
          className="
            card-title
            line-clamp-2
            text-skin-heading

            transition-colors duration-300
            group-hover:text-skin-accent
          "
        >
          {title}
        </h3>

        {/* Description */}
        <p
          className="
            body-2 mt-4
            line-clamp-3
            text-skin-muted
          "
        >
          {description || labels.empty}
        </p>

        {/* ======================================
            CARD FOOTER
        ====================================== */}

        <div className="mt-auto pt-8">

          <div className="mb-5 h-px bg-skin-border" />

          <div
            className="
              flex items-center
              justify-between gap-3
            "
          >
            <span className="button-sm text-skin-heading">
              {labels.explore}
            </span>

            <span
              className="
                flex h-10 w-10
                shrink-0 items-center
                justify-center

                rounded-xl
                border border-skin-border
                bg-skin-soft

                text-skin-heading

                transition-all duration-300

                group-hover:border-brand-navy
                group-hover:bg-brand-navy
                group-hover:text-white

                dark:group-hover:border-white/20
                dark:group-hover:bg-white/15
              "
            >
              <HiOutlineArrowUpRight
                size={19}
                aria-hidden="true"
                className="directional-arrow"
              />
            </span>

          </div>
        </div>
      </div>
    </Link>
  );
}

export default BlogCategoryCard;
